import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Users } from 'lucide-react';

/**
 * Bulk-add Team Members from one or more departments.
 *
 * Picks-then-filters flow:
 *   1. User ticks the department(s) they care about (e.g. Mechanical +
 *      Electrical, skipping Accounting).
 *   2. All employees belonging to those departments render in a
 *      pre-checked list; the user can uncheck individuals they don't
 *      want on this specific project.
 *   3. "Add N members" pushes each ticked employee as a new team row,
 *      auto-filled by ProjectForm's normal updateTeamMember pipeline
 *      (role from position_1, rate from hourly_rate_1, currency).
 */
export default function BulkAddTeamDialog({
  open,
  onOpenChange,
  employees = [],
  departments = [],
  existingTeamMemberIds = [],
  onAdd,
}) {
  const { t } = useTranslation();
  const [pickedDepts, setPickedDepts] = useState(() => new Set());
  const [pickedEmpIds, setPickedEmpIds] = useState(() => new Set());

  // Reset the picker every time the dialog re-opens so a stale
  // selection from a previous project doesn't carry over.
  useEffect(() => {
    if (open) {
      setPickedDepts(new Set());
      setPickedEmpIds(new Set());
    }
  }, [open]);

  const filteredEmployees = useMemo(() => {
    if (pickedDepts.size === 0) return [];
    return employees.filter((e) => e.department && pickedDepts.has(e.department));
  }, [employees, pickedDepts]);

  // Whenever the department picker changes, refresh the pre-checked
  // employee set to exactly those in the filtered list who aren't
  // already on the project.
  useEffect(() => {
    setPickedEmpIds(new Set(
      filteredEmployees
        .filter((e) => !existingTeamMemberIds.includes(e.id))
        .map((e) => e.id)
    ));
  }, [pickedDepts, filteredEmployees, existingTeamMemberIds]);

  const toggleDept = (d) => {
    setPickedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  };

  const toggleEmp = (id) => {
    setPickedEmpIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    const toAdd = filteredEmployees.filter((e) => pickedEmpIds.has(e.id));
    if (toAdd.length === 0) return;
    onAdd(toAdd);
    onOpenChange(false);
  };

  const selectAll = () => setPickedEmpIds(new Set(filteredEmployees.map((e) => e.id)));
  const selectNone = () => setPickedEmpIds(new Set());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-add-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4" /> {t('auto.misc2.bulkAddTeamMembers')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('auto.misc2.departments')}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {departments.length === 0 ? (
                <span className="text-sm text-slate-500">{t('auto.misc2.noDepartmentsDefinedInSettings')}</span>
              ) : departments.map((d) => (
                <label
                  key={d}
                  className={`flex items-center gap-1.5 border rounded-full px-3 py-1 text-sm cursor-pointer transition ${pickedDepts.has(d) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`bulk-dept-${d}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={pickedDepts.has(d)}
                    onChange={() => toggleDept(d)}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Employees ({filteredEmployees.length})
              </Label>
              {filteredEmployees.length > 0 && (
                <div className="flex gap-2 text-xs">
                  <button type="button" className="text-blue-600 hover:underline" onClick={selectAll}>{t('auto.misc2.selectAll')}</button>
                  <span className="text-slate-300">·</span>
                  <button type="button" className="text-slate-500 hover:underline" onClick={selectNone}>{t('auto.misc2.clear')}</button>
                </div>
              )}
            </div>
            <div className="mt-2 max-h-72 overflow-auto border rounded-md divide-y" data-testid="bulk-add-team-employee-list">
              {pickedDepts.size === 0 ? (
                <div className="p-4 text-sm text-slate-500 text-center">{t('auto.misc2.pickOneOrMoreDepartmentsTo')}</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 text-center">{t('auto.misc2.noEmployeesInTheSelectedDepartments')}</div>
              ) : filteredEmployees.map((e) => {
                const already = existingTeamMemberIds.includes(e.id);
                return (
                  <label
                    key={e.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer ${already ? 'opacity-50' : 'hover:bg-slate-50'}`}
                    data-testid={`bulk-emp-${e.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={pickedEmpIds.has(e.id)}
                        disabled={already}
                        onChange={() => toggleEmp(e.id)}
                      />
                      <div>
                        <div className="font-medium text-slate-800">{e.name}</div>
                        <div className="text-xs text-slate-500">{e.department}{e.position_1 ? ` · ${e.position_1}` : e.position ? ` · ${e.position}` : ''}</div>
                      </div>
                    </div>
                    {already && <span className="text-xs text-slate-400 italic">{t('auto.misc2.alreadyOnTeam')}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-add-team-cancel">{t('auto.misc2.cancel')}</Button>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={pickedEmpIds.size === 0}
            data-testid="bulk-add-team-confirm"
          >
            Add {pickedEmpIds.size} member{pickedEmpIds.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
